import type { JobType } from '@lib/enums/job-type.enum'
import type { ProblemSeverity } from '@lib/enums/problem-severity.enum'
import type { TreeMarkColor } from '@lib/enums/tree-mark.enum'
import {
  scheduleRowPriority,
  type TreePriority,
} from '@lib/helpers/proposal-plans'
import type { TreeScheduleRow } from '@lib/dto/proposal-document.dto'
import { UNIDENTIFIED_MARK } from '@lib/enums/tree-mark.enum'
import type { GeoPoint } from '@lib/dto/geo.dto'
import { regionColors, type RegionColor } from '@/map/regionColors'

/**
 * How a tree is drawn on the site plan: a canopy, colored by whichever
 * question the reader is asking.
 */

/** The questions the plan can be colored to answer. */
export type Lens = 'priority' | 'work' | 'species' | 'areas'

export const LENS_LABELS: Record<Lens, string> = {
  priority: 'Priority',
  work: 'Work',
  species: 'Species',
  areas: 'Areas',
}

/** What a tree is shown as, once the client's choices are applied. */
export type TreeStatus = 'in' | 'out'

/**
 * Every tree's canopy on the plan, in meters: a mid-sized street tree, 24
 * feet across.
 *
 * One size for all. Trunks are measured on some trees and not others, and
 * sizing the measured ones from their trunk drew a single measured oak at
 * twice the size of every guess beside it, which a reader takes for a fact
 * about the trees. The circles mark where each tree is; the schedule says
 * what it is.
 */
export const CANOPY_RADIUS_METERS = (24 * 0.3048) / 2

/**
 * Chip classes for a severity. Serious (4) keeps the base clay chip: the
 * stylesheet has no `--4`, and a template string would invent one.
 */
const SEVERITY_CLASS: Record<ProblemSeverity, string | null> = {
  1: 'proposal-severity--1',
  2: 'proposal-severity--2',
  3: 'proposal-severity--3',
  4: null,
  5: 'proposal-severity--5',
}

export function severityClass(severity: ProblemSeverity): string | null {
  return SEVERITY_CLASS[severity]
}

export const PRIORITY_LABELS: Record<TreePriority, string> = {
  hazard: 'Hazard',
  season: 'This season',
  routine: 'Routine',
}

/** Longer wording for the legend. */
export const PRIORITY_LEGEND: Record<TreePriority, string> = {
  hazard: 'Hazard: Serious or Critical',
  season: 'This season: Moderate',
  routine: 'Routine care',
}

const PRIORITY_CLASSES: Record<TreePriority, string> = {
  hazard: 'canopy--hazard',
  season: 'canopy--season',
  routine: 'canopy--routine',
}

/*
 * Literal class names, so Tailwind sees them in source and generates them.
 * The same colors the survey map and the job badges use, so a tree reads the
 * same on the plan as on the badge in its card.
 */
const JOB_CLASSES: Record<JobType, string> = {
  CROWN_CLEANING: 'bg-map-clean',
  CROWN_RAISE: 'bg-map-raise',
  CROWN_RESTORATION: 'bg-map-restore',
  CROWN_THIN: 'bg-map-thin',
  STRUCTURAL_CLEARANCE: 'bg-map-clearance',
  OTHER: 'bg-map-other',
}

/** The same colors as SVG fills, for the static plan. */
const JOB_FILL_CLASSES: Record<JobType, string> = {
  CROWN_CLEANING: 'fill-map-clean',
  CROWN_RAISE: 'fill-map-raise',
  CROWN_RESTORATION: 'fill-map-restore',
  CROWN_THIN: 'fill-map-thin',
  STRUCTURAL_CLEARANCE: 'fill-map-clearance',
  OTHER: 'fill-map-other',
}

const MARK_FILL_CLASSES: Record<TreeMarkColor, string> = {
  ORANGE: 'fill-map-orange',
  VIOLET: 'fill-map-violet',
  GREEN: 'fill-map-green',
  SLATE: 'fill-map-slate',
  ROSE: 'fill-map-rose',
  MAGENTA: 'fill-map-magenta',
  GREY: 'fill-map-grey',
  TEAL: 'fill-map-teal',
  BLUE: 'fill-map-blue',
  CRIMSON: 'fill-map-crimson',
  OLIVE: 'fill-map-olive',
  BROWN: 'fill-map-brown',
}

const MARK_CLASSES: Record<TreeMarkColor, string> = {
  ORANGE: 'bg-map-orange',
  VIOLET: 'bg-map-violet',
  GREEN: 'bg-map-green',
  SLATE: 'bg-map-slate',
  ROSE: 'bg-map-rose',
  MAGENTA: 'bg-map-magenta',
  GREY: 'bg-map-grey',
  TEAL: 'bg-map-teal',
  BLUE: 'bg-map-blue',
  CRIMSON: 'bg-map-crimson',
  OLIVE: 'bg-map-olive',
  BROWN: 'bg-map-brown',
}

/*
 * An area's color, by name. Six band colors used to be handed out in turn,
 * on the belief that six was more areas than a site has; an office park has
 * 29, and its six colors were three pairs of near twins. Now neighbors never
 * share one (docs/map-regions.md).
 */
const AREA_CLASSES: Record<RegionColor, string> = {
  yellow: 'canopy--area-yellow',
  cyan: 'canopy--area-cyan',
  pink: 'canopy--area-pink',
  lime: 'canopy--area-lime',
  orange: 'canopy--area-orange',
  violet: 'canopy--area-violet',
  blue: 'canopy--area-blue',
  coral: 'canopy--area-coral',
}

/** The wash an area takes on the plan under the Areas view. */
export const AREA_WASH_CLASSES: Record<RegionColor, string> = {
  yellow: 'plan-area-wash plan-area-wash--yellow',
  cyan: 'plan-area-wash plan-area-wash--cyan',
  pink: 'plan-area-wash plan-area-wash--pink',
  lime: 'plan-area-wash plan-area-wash--lime',
  orange: 'plan-area-wash plan-area-wash--orange',
  violet: 'plan-area-wash plan-area-wash--violet',
  blue: 'plan-area-wash plan-area-wash--blue',
  coral: 'plan-area-wash plan-area-wash--coral',
}

/**
 * Each area name on a site plan with its color. Rings that share a name
 * are one area to the client, who leaves "Pool" out as one choice, so they
 * share a color, and the legend shows one swatch for them.
 */
export function planAreaColors(
  areas: readonly { name: string; boundary: { vertices: GeoPoint[] } }[],
): Map<string, RegionColor> {
  return regionColors(
    areas.map((area) => ({ key: area.name, ring: area.boundary.vertices })),
  )
}

export function priorityClass(priority: TreePriority): string {
  return PRIORITY_CLASSES[priority]
}

/**
 * Whether a class colors an HTML box (`bg`) or an SVG shape (`fill`). The
 * priority and area classes are written in the stylesheet to do both.
 */
export type Paint = 'bg' | 'fill'

export function jobClass(type: JobType, paint: Paint = 'bg'): string {
  return paint === 'bg' ? JOB_CLASSES[type] : JOB_FILL_CLASSES[type]
}

export function markClass(color: TreeMarkColor, paint: Paint = 'bg'): string {
  return paint === 'bg' ? MARK_CLASSES[color] : MARK_FILL_CLASSES[color]
}

export function areaClass(color: RegionColor): string {
  return AREA_CLASSES[color]
}

/**
 * The job a tree is best summarized by: the first one recorded that is not
 * access and setup, which every tree has and which says nothing about it.
 */
export function primaryJob(row: TreeScheduleRow): JobType | null {
  const named = row.jobs.find((job) => job.type !== 'OTHER')
  return named?.type ?? row.jobs[0]?.type ?? null
}

/** The fill class for one tree under a lens. Null draws it plain. */
export function canopyClass(
  lens: Lens,
  row: TreeScheduleRow,
  areaColor: RegionColor | null,
  paint: Paint = 'bg',
): string {
  switch (lens) {
    case 'priority':
      return priorityClass(scheduleRowPriority(row))
    case 'work': {
      const job = primaryJob(row)
      return job === null ? 'canopy--plain' : jobClass(job, paint)
    }
    case 'species':
      return markClass((row.species ?? UNIDENTIFIED_MARK).color, paint)
    case 'areas':
      return areaColor === null ? 'canopy--plain' : areaClass(areaColor)
  }
}
